import { useMemo, useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownLeft, ArrowUpRight, Bitcoin, Coins, DollarSign, Fuel, Gem, Globe } from "lucide-react";
import { faNum } from "@/lib/format";
import { Sparkline } from "@/components/Sparkline";

const ICONS = { ounce: Globe, silver_ounce: Gem, gold18: Coins, tether: DollarSign, bitcoin: Bitcoin, brent: Fuel };
const TESTIDS = { ounce: "market-card-ounce", silver_ounce: "market-card-silver", gold18: "market-card-gold18", tether: "market-card-tether", bitcoin: "market-card-btc", brent: "market-card-brent" };

const Spark = ({ id, up }) => {
  const pts = useMemo(() => {
    let s = [...id].reduce((a, c) => a + c.charCodeAt(0), 7);
    return Array.from({ length: 14 }, () => {
      s = (s * 9301 + 49297) % 233280;
      return 8 + (s / 233280) * 24;
    });
  }, [id]);
  const line = pts.map((y, i) => `${i === 0 ? "M" : "L"}${(i / (pts.length - 1)) * 100},${40 - y}`).join(" ");
  const color = up ? "#10b981" : "#ef4444";
  return (
    <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="h-12 w-full">
      <defs>
        <linearGradient id={`sg-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${line} L100,40 L0,40 Z`} fill={`url(#sg-${id})`} stroke="none" />
      <motion.path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.6, ease: "easeOut" }}
      />
    </svg>
  );
};

const TiltCard = ({ children, testid }) => {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 160, damping: 18 });
  const sry = useSpring(ry, { stiffness: 160, damping: 18 });
  return (
    <div style={{ perspective: 1000 }}>
      <motion.div
        ref={ref}
        data-testid={testid}
        style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d" }}
        onMouseMove={(e) => {
          const r = ref.current.getBoundingClientRect();
          ry.set(((e.clientX - r.left) / r.width - 0.5) * -12);
          rx.set(((e.clientY - r.top) / r.height - 0.5) * 12);
        }}
        onMouseLeave={() => {
          rx.set(0);
          ry.set(0);
        }}
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="gold-sheen glass rounded-2xl p-5 transition-colors duration-300 hover:border-amber-500/40"
      >
        {children}
      </motion.div>
    </div>
  );
};

export const MarketCards = ({ items, history }) => {
  const list = items?.length ? items : [null, null, null, null, null, null];
  return (
    <section id="market" data-testid="market-cards-grid" className="mx-auto max-w-7xl scroll-mt-28 px-4 py-20 sm:px-6 lg:px-12">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="mb-10 flex flex-wrap items-end justify-between gap-3"
      >
        <div>
          <span className="text-xs tracking-widest text-amber-500/70">شاخص‌های بازار</span>
          <h2 className="mt-2 text-base font-bold text-amber-100 md:text-lg">نبض بازار طلا و ارز</h2>
        </div>
        <span data-testid="market-live-badge" className="flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-[11px] text-emerald-300">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
          </span>
          قیمت زنده · به‌روزرسانی خودکار
        </span>
      </motion.div>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((m, i) =>
          m ? (
            <TiltCard key={m.id} testid={TESTIDS[m.id] || `market-card-${m.id}`}>
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-stone-300">
                  {(() => { const Icon = ICONS[m.id] || Coins; return <Icon className="h-4 w-4 text-amber-400" />; })()}
                  {m.name}
                </div>
                <span
                  data-testid={`market-change-${m.id}`}
                  className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${m.change >= 0 ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"}`}
                >
                  {m.change >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownLeft className="h-3 w-3" />}
                  {faNum(Math.abs(m.change))}٪
                </span>
              </div>
              <div className="mb-3 text-2xl font-bold text-amber-100">
                {faNum(m.price)}
                <span className="mr-2 text-xs font-normal text-stone-500">{m.unit}</span>
              </div>
              {(() => {
                const pts = (history || []).map((h) => h.market?.[m.id]).filter((v) => v);
                return pts.length > 0 ? (
                  <Sparkline testid={`market-spark-${m.id}`} points={pts} width={100} height={40} stroke={m.change >= 0 ? "#10b981" : "#ef4444"} fill className="h-12 w-full" />
                ) : (
                  <Spark id={m.id} up={m.change >= 0} />
                );
              })()}
            </TiltCard>
          ) : (
            <div key={i} className="glass h-44 animate-pulse rounded-2xl" />
          )
        )}
      </div>
    </section>
  );
};
